import { FileDownload } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, MyModal } from "components";
import { ENDPOINTS, SNACKBAR_AUTO_HIDE_MS } from "config/constants";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { MILLISECONDS } from "types";

interface IExportEvents {
  siteId?: string;
  boxId?: string;
}

function toDateInput(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDay(isoDate: string) {
  return new Date(`${isoDate}T00:00:00`);
}

function endOfDay(isoDate: string) {
  return new Date(`${isoDate}T23:59:59.999`);
}

function previousMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
  return { from, to };
}

const SHORTCUTS: { label: string; range: () => { from: Date; to: Date } }[] = [
  {
    label: "Last 24 Hours",
    range: () => ({ from: new Date(Date.now() - MILLISECONDS.DAY), to: new Date() })
  },
  {
    label: "Last Week",
    range: () => ({ from: new Date(Date.now() - MILLISECONDS.WEEK), to: new Date() })
  },
  {
    label: "Last Month",
    range: () => ({ from: new Date(Date.now() - MILLISECONDS.MONTH), to: new Date() })
  },
  {
    label: "Previous Month",
    range: previousMonthRange
  }
];

const dateFieldSx = {
  flex: 1,
  minWidth: 0,
  "& .MuiInputBase-root": {
    bgcolor: COLORS.surface.card,
    color: COLORS.surface.primaryText,
    height: 48,
    borderRadius: `${COLORS.radius.md}px`,
    fontSize: "0.875rem"
  },
  "& .MuiInputBase-input": {
    color: COLORS.surface.primaryText,
    WebkitTextFillColor: COLORS.surface.primaryText,
    colorScheme: "light",
    py: 0
  },
  "& .MuiInputLabel-root": {
    color: COLORS.surface.secondaryText,
    fontSize: "0.875rem",
    fontWeight: 500
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: COLORS.primary
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.surface.border
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.surface.secondaryText
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.primary,
    borderWidth: 1
  }
};

export default function ExportEvents({ siteId, boxId }: IExportEvents) {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState<string | null>("Last Month");
  const defaults = useMemo(() => {
    const { from, to } = SHORTCUTS.find((s) => s.label === "Last Month")!.range();
    return { from: toDateInput(from), to: toDateInput(to) };
  }, []);
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);

  const download = useCallback(
    async (from: Date, to: Date) => {
      if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) {
        enqueueSnackbar(t["Invalid date range"] || "Invalid date range", {
          variant: "error",
          autoHideDuration: SNACKBAR_AUTO_HIDE_MS
        });
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("from", from.toISOString());
        params.set("to", to.toISOString());
        if (siteId) params.set("siteId", siteId);
        if (boxId) params.set("boxId", boxId);
        const res = await fetch(`${ENDPOINTS.threats}/export?${params}`, { credentials: "include" });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.message || t["Export failed"] || "Export failed");
        }
        const blob = await res.blob();
        const cd = res.headers.get("Content-Disposition") || "";
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] || `events-${toDateInput(from)}-${toDateInput(to)}.xls`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setOpen(false);
      } catch (err: any) {
        enqueueSnackbar(err?.message || t["Export failed"] || "Export failed", {
          variant: "error",
          autoHideDuration: SNACKBAR_AUTO_HIDE_MS
        });
      } finally {
        setLoading(false);
      }
    },
    [boxId, siteId, enqueueSnackbar, t]
  );

  const onShortcut = useCallback((label: string) => {
    const shortcut = SHORTCUTS.find((item) => item.label === label);
    if (!shortcut) return;
    const { from, to } = shortcut.range();
    setPreset(label);
    setFromDate(toDateInput(from));
    setToDate(toDateInput(to));
  }, []);

  const onExport = useCallback(() => {
    download(startOfDay(fromDate), endOfDay(toDate));
  }, [download, fromDate, toDate]);

  return (
    <>
      <Tooltip title={t["Export"] || "Export"}>
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label={t["Export"] || "Export"}
          sx={{ color: COLORS.surface.secondaryText }}
        >
          <FileDownload fontSize="small" />
        </IconButton>
      </Tooltip>

      <MyModal title={t["Export"] || "Export"} subtitle={t["Export description"]} open={open} setOpen={setOpen} variant="light">
        <Box component="form" onSubmit={(e) => { e.preventDefault(); onExport(); }}>
          <Stack spacing={2.5}>
            <Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  color: COLORS.surface.secondaryText,
                  fontWeight: 500,
                  fontSize: "0.8125rem",
                  mb: 1.25
                }}
              >
                {t["Quick ranges"] || "Quick ranges"}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
                  gap: 1
                }}
              >
                {SHORTCUTS.map(({ label }) => {
                  const selected = preset === label;
                  return (
                    <Button
                      key={label}
                      size="small"
                      disableElevation
                      disabled={loading}
                      onClick={() => onShortcut(label)}
                      sx={{
                        m: 0,
                        minHeight: 40,
                        py: 0.75,
                        px: 1.5,
                        borderRadius: `${COLORS.radius.md}px`,
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.8125rem",
                        letterSpacing: 0,
                        boxShadow: "none",
                        ...(selected
                          ? {
                              color: COLORS.white,
                              border: "1px solid transparent",
                              backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                              "&:hover": {
                                backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                                filter: "brightness(1.05)",
                                boxShadow: "none"
                              }
                            }
                          : {
                              bgcolor: COLORS.surface.card,
                              color: COLORS.surface.primaryText,
                              border: `1px solid ${COLORS.surface.border}`,
                              "&:hover": {
                                bgcolor: COLORS.surface.tableHeader,
                                borderColor: COLORS.surface.secondaryText,
                                boxShadow: "none"
                              }
                            })
                      }}
                    >
                      {t[label] || label}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  color: COLORS.surface.secondaryText,
                  fontWeight: 500,
                  fontSize: "0.8125rem",
                  mb: 1.25
                }}
              >
                {t["Custom date range"] || "Custom date range"}
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  type="date"
                  label={t["From"] || "From"}
                  value={fromDate}
                  onChange={(e) => {
                    setPreset(null);
                    setFromDate(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: toDate || undefined }}
                  sx={dateFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label={t["To"] || "To"}
                  value={toDate}
                  onChange={(e) => {
                    setPreset(null);
                    setToDate(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: fromDate || undefined }}
                  sx={dateFieldSx}
                />
              </Stack>
            </Box>

            <FormModalActions
              onCancel={() => setOpen(false)}
              cancelLabel={t["Cancel"]}
              submitLabel={t["Export"]}
              loading={loading}
              disabled={!fromDate || !toDate}
              submitIcon={<FileDownload sx={{ fontSize: 18 }} />}
            />
          </Stack>
        </Box>
      </MyModal>
    </>
  );
}
